import { useMemo, useState, useCallback } from 'react';
import { useGetFormDateChangeRequestListQuery, useApproveFormDateChangeRequestMutation } from '../../../../../api';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { usePermissions } from '../../../../../hooks/usePermissions';
import toastService from '../../../../../utils/toastService';
import { FORM_TYPE, DATE_FIELD, DATE_CHANGE_STATUS, DATE_FIELDS_BY_FORM_TYPE } from '../../../../../utils/formDateChangeConstants';

// Tab index -> talep statüsü. Bu modülde "Taslak" kavramı yok; talep doğrudan
// 20 (onay bekliyor) ya da 30 (yönetici/admin açtıysa) ile oluşuyor.
const TAB_STATUS_MAP = {
    0: DATE_CHANGE_STATUS.SENT_FOR_APPROVAL,
    1: DATE_CHANGE_STATUS.RETURN,
    2: DATE_CHANGE_STATUS.APPROVED
};

const INITIAL_FILTERS = {
    chassisNo: null,
    dateField: null,
    startDate: null,
    endDate: null
};

export const useWarrantyDateChangeApprovalData = () => {
    const { th } = useLanguage();
    const { hasWarrantyTeamRole, hasWarrantyManagerRole, hasAdminRole } = usePermissions();

    const canRequestDateChange = hasWarrantyTeamRole() || hasWarrantyManagerRole() || hasAdminRole();
    const canApproveDateChange = hasWarrantyManagerRole() || hasAdminRole();

    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const currentStatus = TAB_STATUS_MAP[activeTabIndex];

    const [formTypeFilter, setFormTypeFilter] = useState(null); // null = Tümü
    const [apiFilters, setApiFilters] = useState(INITIAL_FILTERS);

    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 0 });

    const [rejectDialogRequest, setRejectDialogRequest] = useState(null);

    const filterFields = useMemo(
        () => [
            {
                field: 'chassisNo',
                headerName: th('formDateChangeRequest.filters.chassisNo'),
                type: 'text'
            },
            {
                field: 'dateField',
                headerName: th('formDateChangeRequest.filters.dateField'),
                type: 'select',
                options: (formTypeFilter ? DATE_FIELDS_BY_FORM_TYPE[formTypeFilter] || [] : Object.values(DATE_FIELD)).map((value) => ({
                    label: th(dateFieldLabelKey(value)),
                    value
                }))
            },
            {
                field: 'startDate',
                headerName: th('formDateChangeRequest.filters.startDate'),
                type: 'date'
            },
            {
                field: 'endDate',
                headerName: th('formDateChangeRequest.filters.endDate'),
                type: 'date'
            }
        ],
        [th, formTypeFilter]
    );

    const buildQueryParams = useCallback(
        (status) => {
            const params = {
                status,
                pageNumber: lazyParams.page + 1,
                pageSize: lazyParams.rows
            };
            if (formTypeFilter) params.formType = formTypeFilter;
            if (apiFilters.chassisNo) params.chassisNo = apiFilters.chassisNo;
            if (apiFilters.dateField) params.dateField = apiFilters.dateField;
            if (apiFilters.startDate) params.startDate = toIsoDate(apiFilters.startDate);
            if (apiFilters.endDate) params.endDate = toIsoDate(apiFilters.endDate);
            return params;
        },
        [lazyParams, formTypeFilter, apiFilters]
    );

    const queryParams = useMemo(() => buildQueryParams(currentStatus), [buildQueryParams, currentStatus]);

    const { data, isFetching, refetch } = useGetFormDateChangeRequestListQuery(queryParams);

    // Tab badge sayıları: her statü için ayrı, hafif (pageSize=1) sorgu.
    // RTK Query aynı parametrelerle daha önce çekilmiş listeyi cache'ten kullanır.
    const { data: pendingCountData } = useGetFormDateChangeRequestListQuery({ ...buildQueryParams(DATE_CHANGE_STATUS.SENT_FOR_APPROVAL), pageNumber: 1, pageSize: 1 });
    const { data: returnedCountData } = useGetFormDateChangeRequestListQuery({ ...buildQueryParams(DATE_CHANGE_STATUS.RETURN), pageNumber: 1, pageSize: 1 });
    const { data: approvedCountData } = useGetFormDateChangeRequestListQuery({ ...buildQueryParams(DATE_CHANGE_STATUS.APPROVED), pageNumber: 1, pageSize: 1 });

    const tabCounts = {
        [DATE_CHANGE_STATUS.SENT_FOR_APPROVAL]: pendingCountData?.totalCount ?? 0,
        [DATE_CHANGE_STATUS.RETURN]: returnedCountData?.totalCount ?? 0,
        [DATE_CHANGE_STATUS.APPROVED]: approvedCountData?.totalCount ?? 0
    };

    const items = data?.items ?? [];
    const totalRecords = data?.totalCount ?? 0;

    const [approveRequest, { isLoading: isApproving }] = useApproveFormDateChangeRequestMutation();
    const [rejectRequestMutation, { isLoading: isRejecting }] = useApproveFormDateChangeRequestMutation();

    const handleTabChange = (index) => {
        setActiveTabIndex(index);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 0 }));
    };

    const handleFormTypeChange = (value) => {
        setFormTypeFilter(value);
        setApiFilters((prev) => ({ ...prev, dateField: null }));
        setLazyParams((prev) => ({ ...prev, first: 0, page: 0 }));
    };

    const handleApplyFilters = useCallback((updatedFilters) => {
        setApiFilters({
            chassisNo: updatedFilters.chassisNo || null,
            dateField: updatedFilters.dateField || null,
            startDate: updatedFilters.startDate || null,
            endDate: updatedFilters.endDate || null
        });
        setLazyParams((prev) => ({ ...prev, first: 0, page: 0 }));
    }, []);

    const handleResetFilters = useCallback(() => {
        setApiFilters(INITIAL_FILTERS);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 0 }));
    }, []);

    const onPage = (event) => {
        setLazyParams({ first: event.first, rows: event.rows, page: event.page });
    };

    const [approvingRowId, setApprovingRowId] = useState(null);

    const handleApprove = async (row) => {
        setApprovingRowId(row.id);
        try {
            await approveRequest({ id: row.id, status: DATE_CHANGE_STATUS.APPROVED, returnMessage: null }).unwrap();
            toastService.success(th('formDateChangeRequest.toast.approveSuccess'));
            refetch();
        } catch (error) {
            toastService.error(error?.data?.errorMessage || th('formDateChangeRequest.toast.approveError'));
        } finally {
            setApprovingRowId(null);
        }
    };

    const openRejectDialog = (row) => setRejectDialogRequest(row);
    const closeRejectDialog = () => setRejectDialogRequest(null);

    const handleReject = async (returnMessage) => {
        if (!rejectDialogRequest) return;
        try {
            await rejectRequestMutation({ id: rejectDialogRequest.id, status: DATE_CHANGE_STATUS.RETURN, returnMessage }).unwrap();
            toastService.success(th('formDateChangeRequest.toast.rejectSuccess'));
            closeRejectDialog();
            refetch();
        } catch (error) {
            toastService.error(error?.data?.errorMessage || th('formDateChangeRequest.toast.rejectError'));
        }
    };

    return {
        canRequestDateChange,
        canApproveDateChange,
        activeTabIndex,
        handleTabChange,
        formTypeFilter,
        handleFormTypeChange,
        filterFields,
        handleApplyFilters,
        handleResetFilters,
        lazyParams,
        onPage,
        items,
        totalRecords,
        tabCounts,
        loading: isFetching,
        refetch,
        handleApprove,
        isApproving,
        approvingRowId,
        openRejectDialog,
        closeRejectDialog,
        handleReject,
        isRejecting,
        rejectDialogRequest
    };
};

// FORM_TYPE'ı burada da kullanmak isteyen bileşenler için re-export (index.jsx içinde ayrıca import edilebilir).
export { FORM_TYPE, TAB_STATUS_MAP };

function dateFieldLabelKey(value) {
    switch (value) {
        case DATE_FIELD.VEHICLE_ACCEPT_DATE:
            return 'formDateChangeRequest.dateField.vehicleAcceptDate';
        case DATE_FIELD.STOCK_IN_DATE:
            return 'formDateChangeRequest.dateField.stockInDate';
        case DATE_FIELD.INSPECTION_DATE:
            return 'formDateChangeRequest.dateField.inspectionDate';
        case DATE_FIELD.VEHICLE_REGISTRATION:
            return 'formDateChangeRequest.dateField.vehicleRegistration';
        default:
            return '';
    }
}

function toIsoDate(date) {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return null;
    const timezoneOffset = d.getTimezoneOffset();
    const adjusted = new Date(d.getTime() - timezoneOffset * 60000);
    return adjusted.toISOString();
}
